import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { AppShell } from '@/components/layout/app-shell';
import { ProtectedRoute } from '@/components/layout/protected-route';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { useAuth } from '@/hooks/use-auth';
import { Loader2, Shield, FileText, Camera, Brain } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';

const DashboardPage = lazy(() => import('@/pages/dashboard'));
const JobsPage = lazy(() => import('@/pages/jobs'));
const JobNewPage = lazy(() => import('@/pages/job-new'));
const JobDetailPage = lazy(() => import('@/pages/job-detail'));
const ReportsPage = lazy(() => import('@/pages/reports'));
const InspectionNewPage = lazy(() => import('@/pages/inspection-new'));
const InspectionDetailPage = lazy(() => import('@/pages/inspection-detail'));
const ReportDetailPage = lazy(() => import('@/pages/report-detail'));
const AnalyzerPage = lazy(() => import('@/pages/analyzer'));
const ProfilePage = lazy(() => import('@/pages/profile'));
const AdminPage = lazy(() => import('@/pages/admin'));
const PlatformPage = lazy(() => import('@/pages/platform'));
const TestPanelPage = lazy(() => import('@/pages/test-panel'));

/** Animated structural grid background for the hero panel */
function StructuralGrid() {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path
            d="M 40 0 L 0 0 0 40"
            fill="none"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        </pattern>
        <linearGradient id="fade-up" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="rgba(59,130,246,0.08)" />
          <stop offset="100%" stopColor="rgba(59,130,246,0)" />
        </linearGradient>
        <linearGradient id="node-glow" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#1B2A4A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <rect width="100%" height="40%" y="60%" fill="url(#fade-up)" />

      {/* Animated connection lines */}
      <g className="login-grid-lines">
        <line x1="10%" y1="20%" x2="35%" y2="35%" stroke="rgba(59,130,246,0.15)" strokeWidth="1">
          <animate attributeName="opacity" values="0.3;0.8;0.3" dur="4s" repeatCount="indefinite" />
        </line>
        <line x1="35%" y1="35%" x2="60%" y2="25%" stroke="rgba(59,130,246,0.15)" strokeWidth="1">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="3.5s" repeatCount="indefinite" />
        </line>
        <line x1="60%" y1="25%" x2="85%" y2="45%" stroke="rgba(59,130,246,0.15)" strokeWidth="1">
          <animate attributeName="opacity" values="0.4;0.9;0.4" dur="5s" repeatCount="indefinite" />
        </line>
        <line x1="20%" y1="60%" x2="50%" y2="50%" stroke="rgba(59,130,246,0.1)" strokeWidth="1">
          <animate attributeName="opacity" values="0.2;0.7;0.2" dur="6s" repeatCount="indefinite" />
        </line>
        <line x1="50%" y1="50%" x2="80%" y2="70%" stroke="rgba(59,130,246,0.1)" strokeWidth="1">
          <animate attributeName="opacity" values="0.3;0.6;0.3" dur="4.5s" repeatCount="indefinite" />
        </line>
        <line x1="35%" y1="35%" x2="50%" y2="50%" stroke="rgba(59,130,246,0.12)" strokeWidth="1">
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="3s" repeatCount="indefinite" />
        </line>
        <line x1="15%" y1="75%" x2="40%" y2="85%" stroke="rgba(59,130,246,0.08)" strokeWidth="1">
          <animate attributeName="opacity" values="0.2;0.5;0.2" dur="7s" repeatCount="indefinite" />
        </line>
        <line x1="65%" y1="80%" x2="90%" y2="60%" stroke="rgba(59,130,246,0.08)" strokeWidth="1">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="5.5s" repeatCount="indefinite" />
        </line>
      </g>

      {/* Animated neural nodes */}
      <g className="login-grid-nodes">
        {[
          { cx: '10%', cy: '20%', delay: '0s', dur: '4s' },
          { cx: '35%', cy: '35%', delay: '0.5s', dur: '3.5s' },
          { cx: '60%', cy: '25%', delay: '1s', dur: '5s' },
          { cx: '85%', cy: '45%', delay: '0.3s', dur: '4.5s' },
          { cx: '20%', cy: '60%', delay: '1.5s', dur: '6s' },
          { cx: '50%', cy: '50%', delay: '0.8s', dur: '3s' },
          { cx: '80%', cy: '70%', delay: '2s', dur: '5.5s' },
          { cx: '15%', cy: '75%', delay: '0.2s', dur: '7s' },
          { cx: '40%', cy: '85%', delay: '1.2s', dur: '4s' },
          { cx: '65%', cy: '80%', delay: '0.7s', dur: '5s' },
          { cx: '90%', cy: '60%', delay: '1.8s', dur: '6s' },
        ].map((n, i) => (
          <circle key={i} cx={n.cx} cy={n.cy} r="3" fill="#3B82F6">
            <animate
              attributeName="opacity"
              values="0.3;0.9;0.3"
              dur={n.dur}
              begin={n.delay}
              repeatCount="indefinite"
            />
            <animate
              attributeName="r"
              values="2;4;2"
              dur={n.dur}
              begin={n.delay}
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </g>
    </svg>
  );
}

const FEATURES = [
  { icon: Camera, label: 'Capture', desc: 'Photos & audio on-site' },
  { icon: Brain, label: 'Analyse', desc: 'AI-powered defect detection' },
  { icon: FileText, label: 'Report', desc: 'Professional UK standards' },
  { icon: Shield, label: 'Comply', desc: 'BS/Eurocodes/RICS/IStructE' },
];

function LoginPage() {
  const { user } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger entrance animations after mount
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (user) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <div className="flex min-h-screen">
      {/* Left hero panel — hidden on mobile */}
      <div className="relative hidden lg:flex lg:w-[55%] flex-col justify-between overflow-hidden bg-[#0F1A2E]">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B2A4A] via-[#0F1A2E] to-[#0a1020]" />

        {/* Animated structural grid */}
        <StructuralGrid />

        {/* Radial glow accent */}
        <div className="absolute top-1/4 left-1/3 h-[500px] w-[500px] rounded-full bg-blue-500/[0.04] blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-sky-400/[0.03] blur-[80px]" />

        {/* Content */}
        <div className="relative z-10 flex flex-1 flex-col justify-center px-12 xl:px-20">
          <div
            className="transition-all duration-700 ease-out"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(24px)',
            }}
          >
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-blue-400/80">
              AI-Powered Structural Engineering
            </p>
            <h1 className="text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight text-white">
              Intelligent field reports,{' '}
              <span className="bg-gradient-to-r from-blue-400 to-sky-300 bg-clip-text text-transparent">
                engineered with precision.
              </span>
            </h1>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-slate-400">
              Capture site data. Let AI analyse defects. Generate professional
              reports to UK structural engineering standards — in minutes, not days.
            </p>
          </div>

          {/* Feature pills */}
          <div
            className="mt-12 flex flex-wrap gap-3 transition-all duration-700 delay-300 ease-out"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(16px)',
            }}
          >
            {FEATURES.map((f, i) => (
              <div
                key={f.label}
                className="flex items-center gap-2.5 rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-2 backdrop-blur-sm transition-all duration-500"
                style={{
                  transitionDelay: `${400 + i * 100}ms`,
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(12px)',
                }}
              >
                <f.icon className="h-3.5 w-3.5 text-blue-400" />
                <div className="flex flex-col">
                  <span className="text-[11px] font-semibold text-white/90">{f.label}</span>
                  <span className="text-[10px] text-slate-500">{f.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="relative z-10 border-t border-white/[0.05] px-12 py-5 xl:px-20 transition-all duration-700 delay-500 ease-out"
          style={{
            opacity: mounted ? 1 : 0,
          }}
        >
          <p className="text-[11px] text-slate-500">
            Trusted by structural engineers across the UK &middot; BS EN 1992 &middot; RICS &middot; IStructE
          </p>
        </div>
      </div>

      {/* Right sign-in panel */}
      <div className="relative flex flex-1 flex-col items-center justify-center bg-background px-6 sm:px-12">
        {/* Subtle background texture on light panel */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.35]" />

        <div
          className="relative z-10 w-full max-w-sm transition-all duration-600 ease-out"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          {/* Logo */}
          <div className="mb-10 flex flex-col items-center">
            <img
              src="/structureai-logo.png"
              alt="StructureAI"
              className="h-24 sm:h-28 w-auto object-contain drop-shadow-sm"
            />
            <p className="mt-6 text-center text-sm text-muted-foreground leading-relaxed max-w-[280px]">
              Sign in to access your inspections, reports, and site data.
            </p>
          </div>

          {/* Sign in controls */}
          <GoogleSignInButton />

          {/* Footer */}
          <div className="mt-10 flex flex-col items-center gap-2">
            <div className="h-px w-16 bg-border" />
            <p className="text-[11px] text-muted-foreground/60 text-center leading-relaxed">
              Access is restricted to authorised personnel only.
              <br />
              Contact your administrator if you need an account.
            </p>
          </div>
        </div>

        {/* Mobile-only feature bar at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 border-t border-border/50 bg-background/80 backdrop-blur-sm px-6 py-4 lg:hidden transition-all duration-700 delay-300 ease-out"
          style={{ opacity: mounted ? 1 : 0 }}
        >
          <div className="flex justify-center gap-6">
            {FEATURES.map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-1">
                <f.icon className="h-4 w-4 text-primary/60" />
                <span className="text-[10px] font-medium text-muted-foreground">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6">
        <img
          src="/structureai-logo.png"
          alt="StructureAI"
          className="h-16 w-auto object-contain animate-pulse"
        />
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading page...</p>
      </div>
    </div>
  );
}

export default function App() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />

      <Route
        element={
          <ProtectedRoute>
            <Suspense fallback={<RouteFallback />}>
              <AppShell />
            </Suspense>
          </ProtectedRoute>
        }
      >
        <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
        <Route path={ROUTES.JOBS} element={<JobsPage />} />
        <Route path={ROUTES.JOB_NEW} element={<JobNewPage />} />
        <Route path={ROUTES.JOB_DETAIL} element={<JobDetailPage />} />
        <Route path={ROUTES.INSPECTION_NEW} element={<InspectionNewPage />} />
        <Route path={ROUTES.INSPECTION_DETAIL} element={<InspectionDetailPage />} />
        <Route path={ROUTES.TEST_PANEL_NEW} element={<TestPanelPage />} />
        <Route path={ROUTES.TEST_PANEL_DETAIL} element={<TestPanelPage />} />
        <Route path={ROUTES.REPORTS} element={<ReportsPage />} />
        <Route path={ROUTES.REPORT_DETAIL} element={<ReportDetailPage />} />
        <Route path={ROUTES.ANALYZER} element={<AnalyzerPage />} />
        <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
        <Route path={ROUTES.ADMIN} element={<AdminPage />} />
        <Route path={ROUTES.PLATFORM} element={<PlatformPage />} />
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
}
